import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FaShareAlt } from '@react-icons/all-files/fa/FaShareAlt';
import { FaSpinner } from '@react-icons/all-files/fa/FaSpinner';
import ProfaseModal from '../../../../commons/ProfaseModal';

export interface WorkflowEmpresaResumen {
  id: number;
  rfc?: string;
  razonSocial?: string;
}

interface WorkflowEmpresaDisenoExportModalProps {
  open: boolean;
  workflowEmpresaOrigenId?: string | number;
  workflowId?: string | number;
  origenEmpresaId?: number | null;
  origenEmpresaLabel?: string;
  onClose: () => void;
  onExported?: (cantidad: number) => void;
  showToast: (type: 'success' | 'error', message: string) => void;
}

function parseEmpresasResponse(data: unknown): WorkflowEmpresaResumen[] {
  if (Array.isArray(data)) return data as WorkflowEmpresaResumen[];
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    if (Array.isArray(o.content)) return o.content as WorkflowEmpresaResumen[];
    if (Array.isArray(o.data)) return o.data as WorkflowEmpresaResumen[];
  }
  return [];
}

function extractApiErrorMessage(error: unknown, fallback: string): string {
  const err = error as {
    response?: { data?: { detail?: string; message?: string; title?: string } };
    message?: string;
  };
  const data = err?.response?.data;
  return data?.detail ?? data?.message ?? data?.title ?? err?.message ?? fallback;
}

export default function WorkflowEmpresaDisenoExportModal({
  open,
  workflowEmpresaOrigenId,
  workflowId,
  origenEmpresaId,
  origenEmpresaLabel,
  onClose,
  onExported,
  showToast,
}: WorkflowEmpresaDisenoExportModalProps) {
  const [empresas, setEmpresas] = useState<WorkflowEmpresaResumen[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    if (!open) {
      setEmpresas([]);
      setLoading(false);
      setExporting(false);
      setLoadError(null);
      setSearchQuery('');
      setSelectedIds(new Set());
      return;
    }

    if (workflowId == null) {
      setLoadError('No se pudo determinar el workflow.');
      return;
    }

    setLoading(true);
    setLoadError(null);

    axios
      .get(`api/profase-workflows/${workflowId}/empresas`)
      .then(res => {
        const items = parseEmpresasResponse(res.data)
          .filter(e => e.id != null && e.id !== origenEmpresaId);
        setEmpresas(items);
      })
      .catch(err => {
        setEmpresas([]);
        setLoadError(extractApiErrorMessage(err, 'No se pudieron cargar las empresas del workflow.'));
      })
      .finally(() => setLoading(false));
  }, [open, workflowId, origenEmpresaId]);

  const filteredEmpresas = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return empresas;
    return empresas.filter(e =>
      String(e.id).includes(q)
      || (e.rfc ?? '').toLowerCase().includes(q)
      || (e.razonSocial ?? '').toLowerCase().includes(q),
    );
  }, [empresas, searchQuery]);

  const filteredIds = useMemo(
    () => filteredEmpresas.map(e => e.id),
    [filteredEmpresas],
  );

  const allFilteredSelected = filteredIds.length > 0
    && filteredIds.every(id => selectedIds.has(id));

  const toggleEmpresa = useCallback((empresaId: number, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(empresaId);
      else next.delete(empresaId);
      return next;
    });
  }, []);

  const toggleAllFiltered = useCallback(() => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredIds.forEach(id => next.delete(id));
      } else {
        filteredIds.forEach(id => next.add(id));
      }
      return next;
    });
  }, [allFilteredSelected, filteredIds]);

  const handleExport = async () => {
    if (workflowEmpresaOrigenId == null || selectedIds.size === 0) return;

    setExporting(true);
    try {
      const res = await axios.post<{ cantidad?: number }>(
        `api/profase-workflow-empresas/${workflowEmpresaOrigenId}/exportar`,
        { empresaIds: Array.from(selectedIds) },
      );
      const cantidad = res.data?.cantidad ?? selectedIds.size;
      showToast('success', `Personalización exportada a ${cantidad} empresa${cantidad === 1 ? '' : 's'}.`);
      onExported?.(cantidad);
      onClose();
    } catch (err) {
      showToast('error', extractApiErrorMessage(err, 'No se pudo exportar la personalización.'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <ProfaseModal
      open={open}
      titleId="workflow-empresa-diseno-export-modal-title"
      size="wide"
      scroll
    >
      <div style={s.header}>
        <div>
          <h2 id="workflow-empresa-diseno-export-modal-title" className="profase-modal-title m-0">
            <FaShareAlt style={{ marginRight: 8, verticalAlign: '-2px' }} />
            Exportar diseño personalizado
          </h2>
          {origenEmpresaLabel ? (
            <p style={s.subtitle}>
              Origen: {origenEmpresaLabel}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm"
          onClick={onClose}
          disabled={exporting}
        >
          Cancelar
        </button>
      </div>

      <p style={s.help}>
        Selecciona las empresas destino del mismo workflow. Se copiará el diseño personalizado,
        actividades y configuración asociada.
      </p>

      <input
        type="text"
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        placeholder="Buscar por RFC o razón social…"
        style={s.input}
        aria-label="Filtrar empresas destino"
        autoComplete="off"
        disabled={loading || exporting}
      />

      <div style={s.listBox}>
        {loading ? (
          <div style={s.empty}>
            <FaSpinner style={{ animation: 'spin 0.8s linear infinite', marginRight: 6 }} />
            Cargando empresas…
          </div>
        ) : loadError ? (
          <div style={{ ...s.empty, color: '#dc2626', fontStyle: 'normal' }}>{loadError}</div>
        ) : filteredEmpresas.length === 0 ? (
          <div style={s.empty}>
            {empresas.length === 0
              ? 'No hay otras empresas asociadas a este workflow.'
              : 'Sin resultados para la búsqueda.'}
          </div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, width: 44 }}>
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleAllFiltered}
                    disabled={exporting}
                    aria-label="Seleccionar todas las empresas visibles"
                  />
                </th>
                <th style={s.th}>RFC</th>
                <th style={s.th}>Razón Social</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmpresas.map(item => (
                <tr key={item.id} style={s.tr}>
                  <td style={s.td}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item.id)}
                      onChange={e => toggleEmpresa(item.id, e.target.checked)}
                      disabled={exporting}
                      aria-label={`Seleccionar ${item.rfc ?? item.id}`}
                    />
                  </td>
                  <td style={s.td}>{item.rfc?.trim() || '—'}</td>
                  <td style={s.td}>{item.razonSocial?.trim() || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={s.footer}>
        <span style={s.selectionCount}>
          {selectedIds.size} empresa{selectedIds.size === 1 ? '' : 's'} seleccionada{selectedIds.size === 1 ? '' : 's'}
        </span>
        <button
          type="button"
          style={{
            ...s.btnExport,
            ...(selectedIds.size === 0 || exporting || loading ? s.btnExportDisabled : {}),
          }}
          onClick={() => { void handleExport(); }}
          disabled={selectedIds.size === 0 || exporting || loading || !!loadError}
        >
          {exporting ? (
            <>
              <FaSpinner style={{ animation: 'spin 0.8s linear infinite', marginRight: 6 }} />
              Exportando…
            </>
          ) : (
            'Exportar'
          )}
        </button>
      </div>
    </ProfaseModal>
  );
}

const s: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  subtitle: {
    margin: '6px 0 0',
    fontSize: 13,
    color: 'var(--crm-text-secondary, #64748b)',
  },
  help: {
    margin: '0 0 12px',
    fontSize: 13,
    color: 'var(--crm-text-secondary, #64748b)',
    lineHeight: 1.5,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: 14,
    border: '1px solid var(--crm-border, #e2e8f0)',
    borderRadius: 8,
    background: 'var(--crm-input-bg, #fff)',
    color: 'var(--crm-text-primary, #0f172a)',
    boxSizing: 'border-box',
    marginBottom: 12,
  },
  listBox: {
    maxHeight: 360,
    overflowY: 'auto',
    border: '1px solid var(--crm-border, #e2e8f0)',
    borderRadius: 10,
    background: 'var(--crm-surface, #fff)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  th: {
    padding: '10px 12px',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: 12,
    color: 'var(--crm-text-secondary, #64748b)',
    background: 'var(--crm-grid-header-bg, #f8fafc)',
    borderBottom: '1px solid var(--crm-border, #e2e8f0)',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  tr: {
    borderBottom: '1px solid var(--crm-border, #e2e8f0)',
  },
  td: {
    padding: '10px 12px',
    color: 'var(--crm-text-primary, #0f172a)',
    verticalAlign: 'middle',
  },
  empty: {
    padding: '24px 12px',
    fontSize: 13,
    color: '#94a3b8',
    fontStyle: 'italic',
    textAlign: 'center',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 16,
    flexWrap: 'wrap',
  },
  selectionCount: {
    fontSize: 13,
    color: 'var(--crm-text-secondary, #64748b)',
  },
  btnExport: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '9px 18px',
    background: '#7c3aed',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(124,58,237,0.25)',
  },
  btnExportDisabled: {
    opacity: 0.55,
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
};
